import React, { useState, useEffect } from 'react';
import {
  getPublicImageFolders,
  getPublicImageFiles,
  createPublicImageFolder,
  uploadPublicImage
} from '../api';
import { useTheme } from '../context/ThemeContext';

const PublicImages = () => {
  const { theme } = useTheme();
  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    loadContent();
  }, [currentPath]);

  const loadContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const foldersResponse = await getPublicImageFolders(currentPath);
      setFolders(foldersResponse.data.data || []);
      
      const filesResponse = await getPublicImageFiles(currentPath);
      setFiles(filesResponse.data.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load content');
      console.error('Error loading content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  const handleBack = () => {
    const pathParts = currentPath.split('/');
    pathParts.pop();
    setCurrentPath(pathParts.join('/'));
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      setCreatingFolder(true);
      setError(null);
      const folderPath = currentPath ? `${currentPath}/${newFolderName.trim()}` : newFolderName.trim();
      await createPublicImageFolder(folderPath);
      setShowCreateFolder(false);
      setNewFolderName('');
      await loadContent();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create folder');
      console.error(err);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      setUploadingFile(true);
      setError(null);
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (currentPath) {
        formData.append('folderPath', currentPath);
      }

      await uploadPublicImage(formData);
      setShowUploadFile(false);
      setSelectedFile(null);
      await loadContent();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to upload file');
      console.error(err);
    } finally {
      setUploadingFile(false);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const getPublicUrl = (file) => {
    return file.publicUrl || `https://storage.googleapis.com/[REDACTED]-public/${file.fullPath}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#2c3e50', marginBottom: '10px' }}>
          Public Images
        </h1>
        <p style={{ color: '#7f8c8d', margin: 0 }}>
          Manage public images in GCP Cloud Storage bucket: <strong>[REDACTED]-public</strong>
        </p>
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '15px 20px',
        borderRadius: '5px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <button
          onClick={() => setCurrentPath('')}
          style={{
            padding: '5px 10px',
            border: 'none',
            backgroundColor: currentPath ? '#3498db' : '#95a5a6',
            color: 'white',
            borderRadius: '3px',
            cursor: currentPath ? 'pointer' : 'not-allowed',
            fontSize: '12px'
          }}
          disabled={!currentPath}
        >
          Root
        </button>
        {currentPath && (
          <>
            <span style={{ color: '#7f8c8d' }}>/</span>
            <span style={{ color: '#2c3e50' }}>{currentPath}</span>
            <button
              onClick={handleBack}
              style={{
                marginLeft: 'auto',
                padding: '5px 15px',
                border: 'none',
                backgroundColor: '#e74c3c',
                color: 'white',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              ← Back
            </button>
          </>
        )}
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee',
          color: '#c33',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
          border: '1px solid #fcc'
        }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              color: '#c33',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px'
      }}>
        <button
          onClick={() => setShowCreateFolder(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          + Create Folder
        </button>
        <button
          onClick={() => setShowUploadFile(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          📤 Upload File
        </button>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '5px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        minHeight: '400px'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
            Loading...
          </div>
        ) : (
          <div style={{ padding: '20px' }}>
            {folders.length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <h2 style={{ fontSize: '18px', color: '#2c3e50', marginBottom: '15px' }}>
                  Folders ({folders.length})
                </h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '15px'
                }}>
                  {folders.map((folder, index) => (
                    <div
                      key={index}
                      onClick={() => handleFolderClick(folder)}
                      style={{
                        padding: '15px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: '#f8f9fa'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e9ecef';
                        e.currentTarget.style.borderColor = '#3498db';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#ddd';
                      }}
                    >
                      <div style={{ fontSize: '32px', marginBottom: '10px' }}>📁</div>
                      <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '5px' }}>
                        {folder}
                      </div>
                      <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                        Click to open
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {files.length > 0 && (
              <div>
                <h2 style={{ fontSize: '18px', color: '#2c3e50', marginBottom: '15px' }}>
                  Files ({files.length})
                </h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: '15px'
                }}>
                  {files.map((file, index) => {
                    const publicUrl = getPublicUrl(file);
                    const isImage = file.contentType && file.contentType.startsWith('image/');
                    
                    return (
                      <div
                        key={index}
                        style={{
                          padding: '15px',
                          border: '1px solid #ddd',
                          borderRadius: '5px',
                          backgroundColor: '#fff'
                        }}
                      >
                        {isImage && (
                          <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                            <img
                              src={publicUrl}
                              alt={file.name}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '150px',
                                borderRadius: '5px',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        )}
                        <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '5px', wordBreak: 'break-word' }}>
                          {file.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '10px' }}>
                          Size: {formatFileSize(file.size || 0)}<br />
                          Updated: {formatDate(file.updated)}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '5px' }}>
                            Public URL:
                          </div>
                          <div style={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'center'
                          }}>
                            <input
                              type="text"
                              value={publicUrl}
                              readOnly
                              style={{
                                flex: 1,
                                padding: '5px',
                                border: '1px solid #ddd',
                                borderRadius: '3px',
                                fontSize: '11px',
                                fontFamily: 'monospace'
                              }}
                            />
                            <button
                              onClick={() => copyToClipboard(publicUrl)}
                              style={{
                                padding: '5px 10px',
                                backgroundColor: copiedUrl === publicUrl ? '#27ae60' : '#3498db',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '11px'
                              }}
                            >
                              {copiedUrl === publicUrl ? '✓' : '📋'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!loading && folders.length === 0 && files.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
                No folders or files found. Create a folder or upload a file to get started.
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateFolder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: theme.cardBackground,
            padding: '30px',
            borderRadius: '10px',
            width: '400px',
            boxShadow: `0 4px 6px ${theme.cardShadow}`,
            border: `1px solid ${theme.border}`
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: theme.text }}>Create Folder</h2>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              disabled={creatingFolder}
              style={{
                width: '100%',
                padding: '10px',
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: '5px',
                fontSize: '14px',
                marginBottom: '20px',
                boxSizing: 'border-box',
                opacity: creatingFolder ? 0.6 : 1,
                cursor: creatingFolder ? 'not-allowed' : 'text',
                backgroundColor: theme.inputBackground,
                color: theme.text
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !creatingFolder && newFolderName.trim()) {
                  handleCreateFolder();
                }
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  if (!creatingFolder) {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                  }
                }}
                disabled={creatingFolder}
                style={{
                  padding: '10px 20px',
                  backgroundColor: creatingFolder ? '#bdc3c7' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: creatingFolder ? 'not-allowed' : 'pointer',
                  opacity: creatingFolder ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={creatingFolder || !newFolderName.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: creatingFolder ? '#7f8c8d' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: (creatingFolder || !newFolderName.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (creatingFolder || !newFolderName.trim()) ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '100px',
                  justifyContent: 'center'
                }}
              >
                {creatingFolder ? (
                  <>
                    <div style={{
                      width: '14px',
                      height: '14px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }}></div>
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadFile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: theme.cardBackground,
            padding: '30px',
            borderRadius: '10px',
            width: '400px',
            boxShadow: `0 4px 6px ${theme.cardShadow}`,
            border: `1px solid ${theme.border}`
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: theme.text }}>Upload File</h2>
            <input
              type="file"
              onChange={handleFileSelect}
              disabled={uploadingFile}
              style={{
                width: '100%',
                padding: '10px',
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: '5px',
                fontSize: '14px',
                marginBottom: '20px',
                boxSizing: 'border-box',
                opacity: uploadingFile ? 0.6 : 1,
                cursor: uploadingFile ? 'not-allowed' : 'pointer',
                backgroundColor: theme.inputBackground,
                color: theme.text
              }}
            />
            {selectedFile && (
              <div style={{
                padding: '10px',
                backgroundColor: theme.tableHeader,
                borderRadius: '5px',
                marginBottom: '20px',
                fontSize: '14px',
                color: theme.text
              }}>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  if (!uploadingFile) {
                    setShowUploadFile(false);
                    setSelectedFile(null);
                  }
                }}
                disabled={uploadingFile}
                style={{
                  padding: '10px 20px',
                  backgroundColor: uploadingFile ? '#bdc3c7' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: uploadingFile ? 'not-allowed' : 'pointer',
                  opacity: uploadingFile ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadFile}
                disabled={!selectedFile || uploadingFile}
                style={{
                  padding: '10px 20px',
                  backgroundColor: uploadingFile ? '#7f8c8d' : (selectedFile ? '#3498db' : '#95a5a6'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: (!selectedFile || uploadingFile) ? 'not-allowed' : 'pointer',
                  opacity: (!selectedFile || uploadingFile) ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '100px',
                  justifyContent: 'center'
                }}
              >
                {uploadingFile ? (
                  <>
                    <div style={{
                      width: '14px',
                      height: '14px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }}></div>
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PublicImages;
